import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import PageMeta from '../components/PageMeta';
import { getMatchLogos, getCompetitionLogo } from '../utils/teamLogos';
import { getMergedSectorStringsForHome, getZoneFromSector } from '../utils/stadiumSectorGroups';
import {
  buildCompanionAttendeeFromProfile,
  buildPrimaryAttendeeFromUser,
  emptyCompanionAttendee,
  getTicketOrderContext,
  isCompanionAttendeeComplete,
} from '../utils/subscriberTicketRules';
import { formatMatchDate } from '../utils/matchDateTime';
import { isIdentityExpired, orderIncludesPrimaryTicket } from '../utils/identityExpiry';
import { getMembershipCodesForSeason } from '../utils/membershipCodes';
import { MEMBERSHIP_SEASON } from '../config/membershipSeason';
import { translations } from '../i18n/translations';
import stadiumPlan from '../assets/images/mappe_allianz_stadium.png';
import TicketOrderTravelForm from '../components/TicketOrderTravelForm';
import { requiresTravelInformationForm } from '../utils/europeanAwayTravel';
import {
  computeTicketOrderPricing,
  getAwayMatchPricing,
  getSectorPricing,
} from '../utils/ticketMinorPricing';
import './TicketPayment.css';

const TicketPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, locale, language } = useLanguage();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkingExistingOrder, setCheckingExistingOrder] = useState(false);
  const [existingOrder, setExistingOrder] = useState(null);
  const [includeCompanion, setIncludeCompanion] = useState(false);
  const [companionAttendees, setCompanionAttendees] = useState([emptyCompanionAttendee()]);
  const [companionEcardLoading, setCompanionEcardLoading] = useState({});
  const [area, setArea] = useState('');
  const [sector, setSector] = useState('');
  const [primaryEcard, setPrimaryEcard] = useState('');
  const [primaryPnr, setPrimaryPnr] = useState('');
  const [hoveredSector, setHoveredSector] = useState(null);
  const [hoveredAreaCoords, setHoveredAreaCoords] = useState(null);
  // Image is 775x615 pixels (from actual file dimensions - mappe_allianz_stadium.png)
  const [imageDimensions, setImageDimensions] = useState({ width: 775, height: 615 });
  const [displayDimensions, setDisplayDimensions] = useState({ width: 775, height: 615 });
  const [svgDimensions, setSvgDimensions] = useState({ width: 775, height: 615 });
  const stadiumImageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('CHF');
  const travelFormRef = useRef(null);

  useEffect(() => {
    if (paymentMethod === 'Twint') {
      setPaymentCurrency('CHF');
    }
  }, [paymentMethod]);

  useEffect(() => {
    // Get ticket/match data from location state
    if (location.state?.ticket) {
      setTicket(location.state.ticket);
      setLoading(false);
    } else {
      // If no ticket data, try to fetch available tickets or redirect
      // For now, redirect to tickets page
      navigate('/billets');
    }
  }, [location, navigate]);

  useEffect(() => {
    const checkExistingOrder = async () => {
      if (!user || !ticket?._id) {
        setExistingOrder(null);
        return;
      }
      try {
        setCheckingExistingOrder(true);
        const res = await api.get(`/ticket-orders/check/${ticket._id}`);
        setExistingOrder(res.data?.alreadyOrdered ? res.data.order : null);
      } catch (error) {
        console.error('Error checking existing ticket order:', error);
        setExistingOrder(null);
      } finally {
        setCheckingExistingOrder(false);
      }
    };
    checkExistingOrder();
  }, [user, ticket?._id]);

  const orderContext = useMemo(
    () => (ticket && user ? getTicketOrderContext(user, ticket) : null),
    [ticket, user],
  );
  const travelFormRequired = useMemo(
    () => (ticket ? requiresTravelInformationForm(ticket) : false),
    [ticket],
  );
  const showPrimaryTicket = orderContext?.showPrimaryTicket ?? true;
  const isSubscriber = orderContext?.isSubscriber ?? false;

  useEffect(() => {
    setIncludeCompanion(!showPrimaryTicket);
  }, [showPrimaryTicket]);

  useEffect(() => {
    if (!user) return;
    const codes = getMembershipCodesForSeason(user, MEMBERSHIP_SEASON);
    setPrimaryEcard(codes.ecardCode || '');
    setPrimaryPnr(codes.pnrCode || '');
  }, [user]);

  const activeCompanionCount = includeCompanion ? companionAttendees.length : 0;
  const orderQuantity = (showPrimaryTicket ? 1 : 0) + activeCompanionCount;

  const identityExpiredForPrimary =
    user && orderIncludesPrimaryTicket(showPrimaryTicket, orderQuantity) && isIdentityExpired(user);

  const identityExpiredMessage = identityExpiredForPrimary
    ? (user?.preferredLanguage === 'it'
        ? translations.it.ticketPayment.alertIdentityExpired
        : translations.fr.ticketPayment.alertIdentityExpired)
    : '';

  const addCompanionForm = () => {
    setIncludeCompanion(true);
    setCompanionAttendees((prev) => [...prev, emptyCompanionAttendee()]);
  };

  const removeCompanionForm = (index) => {
    setCompanionAttendees((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        setIncludeCompanion(false);
        return [emptyCompanionAttendee()];
      }
      return next;
    });
  };

  // Detect mobile device and update display dimensions when image loads or window resizes
  useEffect(() => {
    const checkMobile = () => {
      const wasMobile = isMobile;
      const nowMobile = window.innerWidth <= 768;
      setIsMobile(nowMobile);
      
      // Update SVG dimensions for mobile based on viewport width
      if (nowMobile && imageLoaded) {
        const baseViewportWidth = 390; // iPhone 12 Pro width
        const baseSvgWidth = 719;
        const baseSvgHeight = 580;
        const currentViewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const scaleFactor = currentViewportWidth / baseViewportWidth;
        // Calculate SVG dimensions based on viewport, not image size
        const svgWidth = Math.round(baseSvgWidth * scaleFactor);
        const svgHeight = Math.round(baseSvgHeight * scaleFactor);
        
        setSvgDimensions({
          width: svgWidth,
          height: svgHeight
        });
      }
      
      // If mobile state changed, trigger dimension update
      if (wasMobile !== nowMobile && imageLoaded && stadiumImageRef.current) {
        setTimeout(() => {
          const img = stadiumImageRef.current;
          if (img) {
            void img.offsetWidth; // Force reflow
            const displayWidth = img.offsetWidth || img.naturalWidth || 775;
            const displayHeight = img.offsetHeight || img.naturalHeight || 615;
            setDisplayDimensions({
              width: displayWidth,
              height: displayHeight
            });
          }
        }, 100);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, [isMobile, imageLoaded]);

  // Update display dimensions when image loads or window resizes
  useEffect(() => {
    if (!imageLoaded || !stadiumImageRef.current) return;

    const updateDimensions = () => {
      const img = stadiumImageRef.current;
      if (img) {
        // Force a reflow to get accurate dimensions
        void img.offsetWidth;
        // Use getBoundingClientRect for accurate displayed size
        const rect = img.getBoundingClientRect();
        const displayWidth = rect.width || img.clientWidth || img.offsetWidth || img.naturalWidth || 775;
        const displayHeight = rect.height || img.clientHeight || img.offsetHeight || img.naturalHeight || 615;
        
        setDisplayDimensions({
          width: displayWidth,
          height: displayHeight
        });
        
        // Calculate responsive SVG dimensions for mobile
        if (isMobile) {
          const baseViewportWidth = 390; // iPhone 12 Pro width
          const baseSvgWidth = 719;
          const baseSvgHeight = 580;
          const currentViewportWidth = window.innerWidth || document.documentElement.clientWidth;
          const scaleFactor = currentViewportWidth / baseViewportWidth;
          
          // Calculate SVG dimensions based on viewport, not image size
          const svgWidth = Math.round(baseSvgWidth * scaleFactor);
          const svgHeight = Math.round(baseSvgHeight * scaleFactor);
          
          setSvgDimensions({
            width: svgWidth,
            height: svgHeight
          });
        } else {
          setSvgDimensions({
            width: displayWidth,
            height: displayHeight
          });
        }
        
        // Trigger map coordinate update after dimensions are set
        setTimeout(() => {
          const mapElement = document.getElementById('stadium-map');
          if (mapElement && img) {
            const originalWidth = imageDimensions.width;
            const originalHeight = imageDimensions.height;
            const actualWidth = img.clientWidth || img.offsetWidth || img.naturalWidth || originalWidth;
            const actualHeight = img.clientHeight || img.offsetHeight || img.naturalHeight || originalHeight;
            const scaleX = actualWidth / originalWidth;
            const scaleY = actualHeight / originalHeight;

            const areas = mapElement.querySelectorAll('area');
            areas.forEach(area => {
              let originalCoords = area.getAttribute('data-original-coords');
              if (!originalCoords) {
                originalCoords = area.getAttribute('coords');
                area.setAttribute('data-original-coords', originalCoords);
              }

              const shape = area.getAttribute('shape');
              const coordsArray = originalCoords.split(',').map(c => parseFloat(c.trim()));
              
              const scaledCoords = coordsArray.map((coord, index) => {
                if (shape === 'rect') {
                  return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
                } else if (shape === 'poly') {
                  return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
                } else if (shape === 'circle') {
                  if (index === 2) {
                    return Math.round(coord * ((scaleX + scaleY) / 2));
                  }
                  return index === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
                }
                return coord;
              });

              area.setAttribute('coords', scaledCoords.join(','));
            });
          }
        }, 200);
      }
    };

    // Update dimensions initially with a delay to ensure image is rendered
    const timeoutId = setTimeout(updateDimensions, 100);

    // Add resize listener
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
    };
  }, [imageLoaded, isMobile, imageDimensions]);

  // Helper function to parse coords and return shape info
  const parseAreaCoords = useCallback((coords, shape) => {
    if (!coords) return null;
    const coordsArray = coords.split(',').map(c => parseFloat(c.trim()));
    
    if (shape === 'rect') {
      // Rect format: x1,y1,x2,y2
      const x1 = coordsArray[0];
      const y1 = coordsArray[1];
      const x2 = coordsArray[2];
      const y2 = coordsArray[3];
      return {
        type: 'rect',
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1)
      };
    } else if (shape === 'poly') {
      // Poly format: x1,y1,x2,y2,x3,y3,...
      const points = [];
      for (let i = 0; i < coordsArray.length; i += 2) {
        if (i + 1 < coordsArray.length) {
          points.push({ x: coordsArray[i], y: coordsArray[i + 1] });
        }
      }
      return {
        type: 'poly',
        points: points
      };
    }
    
    return null;
  }, []);

  // Get available sectors (with prices) from ticket
  const getAvailableSectors = useCallback(() => {
    if (!ticket || !ticket.areaPricing) return new Set();
    const available = new Set();
    ticket.areaPricing.forEach(pricing => {
      if (pricing.hasSectors && pricing.sectors) {
        pricing.sectors.forEach(sector => {
          available.add(sector.name);
        });
      }
    });
    return available;
  }, [ticket]);

  const getSectorPrice = useCallback(
    (sectorName) => getSectorPricing(ticket, sectorName).price,
    [ticket],
  );

  // Function to check if a point is inside an area
  const isPointInArea = useCallback((x, y, area) => {
    const shape = area.getAttribute('shape');
    const coordsStr = area.getAttribute('data-original-coords') || area.getAttribute('coords');
    if (!coordsStr) return false;
    
    const coords = coordsStr.split(',').map(c => parseFloat(c.trim())).filter(c => !isNaN(c));
    
    if (shape === 'rect' && coords.length >= 4) {
      // Rect: x1,y1,x2,y2
      const x1 = Math.min(coords[0], coords[2]);
      const y1 = Math.min(coords[1], coords[3]);
      const x2 = Math.max(coords[0], coords[2]);
      const y2 = Math.max(coords[1], coords[3]);
      return x >= x1 && x <= x2 && y >= y1 && y <= y2;
    } else if (shape === 'poly' && coords.length >= 6) {
      // Poly: x1,y1,x2,y2,x3,y3,...
      // Use ray casting algorithm
      let inside = false;
      for (let i = 0, j = coords.length - 2; i < coords.length; j = i, i += 2) {
        const xi = coords[i], yi = coords[i + 1];
        const xj = coords[j], yj = coords[j + 1];
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    } else if (shape === 'circle' && coords.length >= 3) {
      // Circle: x,y,radius
      const cx = coords[0];
      const cy = coords[1];
      const r = coords[2];
      const dx = x - cx;
      const dy = y - cy;
      return (dx * dx + dy * dy) <= (r * r);
    }
    return false;
  }, []);

  // Handle click/touch directly on image (for mobile)
  const handleImageClick = useCallback((e) => {
    if (!stadiumImageRef.current || !imageLoaded) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const img = stadiumImageRef.current;
    const rect = img.getBoundingClientRect();
    
    // Use natural dimensions as the original coordinate system
    const originalWidth = img.naturalWidth || imageDimensions.width || 775;
    const originalHeight = img.naturalHeight || imageDimensions.height || 615;
    
    // Get click coordinates relative to image (accounting for any padding/borders)
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    // Convert to original coordinate system (natural image size)
    const scaleX = originalWidth / rect.width;
    const scaleY = originalHeight / rect.height;
    const originalX = clickX * scaleX;
    const originalY = clickY * scaleY;
    
    console.log('Click detected:', {
      click: { x: clickX, y: clickY },
      rect: { width: rect.width, height: rect.height },
      natural: { width: originalWidth, height: originalHeight },
      converted: { x: originalX, y: originalY },
      scale: { x: scaleX, y: scaleY }
    });
    
    // Find which area contains this point
    const mapElement = document.getElementById('stadium-map');
    if (!mapElement) return;
    
    const areas = mapElement.querySelectorAll('area');
    const availableSectors = getAvailableSectors();
    
    for (const area of areas) {
      // Always use original coords for calculation
      let originalCoords = area.getAttribute('data-original-coords');
      if (!originalCoords) {
        originalCoords = area.getAttribute('coords');
        if (originalCoords) {
          area.setAttribute('data-original-coords', originalCoords);
        } else {
          continue;
        }
      }
      
      const sectorNumStr = area.getAttribute('title');
      const isLegendsClub = sectorNumStr && sectorNumStr.toLowerCase().includes('legends');
      const sectorNum = parseInt(sectorNumStr);
      const isValidSector = !isNaN(sectorNum) && sectorNum > 0;
      
      if (!isLegendsClub && !isValidSector) continue;
      
      const sectorKey = isLegendsClub ? 'Legends club' : sectorNumStr;
      if (!availableSectors.has(sectorKey)) continue;
      
      if (isPointInArea(originalX, originalY, area)) {
        console.log('Sector found:', sectorKey, 'at coords:', originalX, originalY);
        if (isLegendsClub) {
          setSector('Legends club');
          setArea(getZoneFromSector('Legends club'));
        } else {
          const sectorStr = sectorNum.toString();
          setSector(sectorStr);
          const zone = getZoneFromSector(sectorNum);
          setArea(zone);
        }
        break;
      }
    }
  }, [imageLoaded, imageDimensions, getAvailableSectors, getZoneFromSector, isPointInArea]);

  const handleImageTouch = useCallback((e) => {
    if (!stadiumImageRef.current || !imageLoaded) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0] || e.changedTouches[0];
    if (!touch) return;
    
    const img = stadiumImageRef.current;
    const rect = img.getBoundingClientRect();
    
    // Use natural dimensions as the original coordinate system
    const originalWidth = img.naturalWidth || imageDimensions.width || 775;
    const originalHeight = img.naturalHeight || imageDimensions.height || 615;
    
    // Get touch coordinates relative to image (accounting for any padding/borders)
    const touchX = touch.clientX - rect.left;
    const touchY = touch.clientY - rect.top;
    
    // Ensure coordinates are within image bounds
    if (touchX < 0 || touchX > rect.width || touchY < 0 || touchY > rect.height) {
      console.log('Touch outside image bounds');
      return;
    }
    
    // Convert to original coordinate system (natural image size)
    // The map coordinates are based on naturalWidth x naturalHeight
    const scaleX = originalWidth / rect.width;
    const scaleY = originalHeight / rect.height;
    const originalX = touchX * scaleX;
    const originalY = touchY * scaleY;
    
    console.log('Touch detected:', {
      touch: { x: touchX, y: touchY },
      rect: { width: rect.width, height: rect.height },
      natural: { width: originalWidth, height: originalHeight },
      converted: { x: originalX, y: originalY },
      scale: { x: scaleX, y: scaleY }
    });
    
    // Find which area contains this point
    const mapElement = document.getElementById('stadium-map');
    if (!mapElement) return;
    
    const areas = mapElement.querySelectorAll('area');
    const availableSectors = getAvailableSectors();
    
    for (const area of areas) {
      // Always use original coords for calculation
      let originalCoords = area.getAttribute('data-original-coords');
      if (!originalCoords) {
        originalCoords = area.getAttribute('coords');
        if (originalCoords) {
          area.setAttribute('data-original-coords', originalCoords);
        } else {
          continue;
        }
      }
      
      const sectorNumStr = area.getAttribute('title');
      const isLegendsClub = sectorNumStr && sectorNumStr.toLowerCase().includes('legends');
      const sectorNum = parseInt(sectorNumStr);
      const isValidSector = !isNaN(sectorNum) && sectorNum > 0;
      
      if (!isLegendsClub && !isValidSector) continue;
      
      const sectorKey = isLegendsClub ? 'Legends club' : sectorNumStr;
      if (!availableSectors.has(sectorKey)) continue;
      
      if (isPointInArea(originalX, originalY, area)) {
        console.log('Sector found:', sectorKey, 'at coords:', originalX, originalY);
        if (isLegendsClub) {
          setSector('Legends club');
          setArea(getZoneFromSector('Legends club'));
        } else {
          const sectorStr = sectorNum.toString();
          setSector(sectorStr);
          const zone = getZoneFromSector(sectorNum);
          setArea(zone);
        }
        break;
      }
    }
  }, [imageLoaded, imageDimensions, getAvailableSectors, getZoneFromSector, isPointInArea]);

  // Function to update map coordinates based on image scale
  const updateMapCoordinates = useCallback(() => {
    if (!stadiumImageRef.current || !imageLoaded) return;
    
    const img = stadiumImageRef.current;
    const mapElement = document.getElementById('stadium-map');
    if (!mapElement) return;

    // Get actual displayed dimensions - use getBoundingClientRect for most accurate size
    const rect = img.getBoundingClientRect();
    const displayedWidth = rect.width || img.clientWidth || img.offsetWidth || img.naturalWidth || 775;
    const displayedHeight = rect.height || img.clientHeight || img.offsetHeight || img.naturalHeight || 615;
    
    const originalWidth = imageDimensions.width; // 775
    const originalHeight = imageDimensions.height; // 615

    // Calculate scale factors
    const scaleX = displayedWidth / originalWidth;
    const scaleY = displayedHeight / originalHeight;

    const areas = mapElement.querySelectorAll('area');
    areas.forEach(area => {
      let originalCoords = area.getAttribute('data-original-coords');
      
      // Store original coords if not already stored
      if (!originalCoords) {
        originalCoords = area.getAttribute('coords');
        if (originalCoords) {
          area.setAttribute('data-original-coords', originalCoords);
        } else {
          return; // Skip if no coords
        }
      }

      const shape = area.getAttribute('shape');
      const coordsArray = originalCoords.split(',').map(c => parseFloat(c.trim())).filter(c => !isNaN(c));
      
      if (coordsArray.length === 0) return;
      
      // Scale coordinates based on shape
      const scaledCoords = coordsArray.map((coord, index) => {
        if (shape === 'rect') {
          // For rect: x1,y1,x2,y2 - scale x and y separately
          return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
        } else if (shape === 'poly') {
          // For poly: x1,y1,x2,y2,x3,y3,... - scale x and y separately
          return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
        } else if (shape === 'circle') {
          // For circle: x,y,radius - scale x, y, and radius
          if (index === 2) {
            // radius - use average scale
            return Math.round(coord * ((scaleX + scaleY) / 2));
          }
          return index === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
        }
        return coord;
      });

      // Update the coords attribute
      area.setAttribute('coords', scaledCoords.join(','));
    });
  }, [imageLoaded, imageDimensions]);

  // Update map coordinates when image size changes
  useEffect(() => {
    if (!imageLoaded) return;
    
    // Multiple attempts to ensure coordinates are updated after image is fully rendered
    const updateWithDelay = (delay) => {
      setTimeout(() => {
        updateMapCoordinates();
      }, delay);
    };
    
    updateWithDelay(50);
    updateWithDelay(200);
    updateWithDelay(500);
    
    // Also update on window resize
    const handleResize = () => {
      setTimeout(() => updateMapCoordinates(), 100);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [imageLoaded, displayDimensions, isMobile, updateMapCoordinates]);

  // Store original coordinates for all areas
  useEffect(() => {
    if (!imageLoaded) return;

    const mapElement = document.getElementById('stadium-map');
    if (!mapElement) return;

    const areas = mapElement.querySelectorAll('area');
    areas.forEach(area => {
      if (!area.getAttribute('data-original-coords')) {
        const coords = area.getAttribute('coords');
        if (coords) {
          area.setAttribute('data-original-coords', coords);
        }
      }
    });
  }, [imageLoaded]);

  // Set up image map area click and hover handlers
  useEffect(() => {
    if (!imageLoaded || !ticket) return;

    const mapElement = document.getElementById('stadium-map');
    if (!mapElement) return;

    const areas = mapElement.querySelectorAll('area');
    const availableSectors = getAvailableSectors();
    
    // Create click and hover handlers for each area
    const clickHandlers = new Map();
    const touchHandlers = new Map();
    const mouseEnterHandlers = new Map();
    const mouseLeaveHandlers = new Map();
    
    areas.forEach(area => {
      const sectorNumStr = area.getAttribute('title');
      const sectorNum = parseInt(sectorNumStr);
      
      const coords = area.getAttribute('coords');
      const shape = area.getAttribute('shape');
      
      // Check if this is "Legends club" or a valid sector number
      const isLegendsClub = sectorNumStr && sectorNumStr.toLowerCase().includes('legends');
      const isValidSector = !isNaN(sectorNum) && sectorNum > 0;
      
      // Skip areas that are neither Legends club nor valid sectors
      if (!isLegendsClub && !isValidSector) {
        area.setAttribute('href', 'javascript:void(0)');
        return;
      }

      // Check if sector is available (has price)
      const sectorKey = isLegendsClub ? 'Legends club' : sectorNumStr;
      const isAvailable = availableSectors.has(sectorKey);
      
      // Disable unavailable sectors
      if (!isAvailable) {
        area.style.cursor = 'not-allowed';
        area.style.opacity = '0.5';
        area.setAttribute('href', 'javascript:void(0)');
        return;
      }
      
      const handleAreaClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isLegendsClub) {
          setSector('Legends club');
          setArea(getZoneFromSector('Legends club'));
        } else {
          const sectorStr = sectorNum.toString();
          setSector(sectorStr);
          const zone = getZoneFromSector(sectorNum);
          setArea(zone);
        }
      };
      
      const handleAreaTouch = (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateMapCoordinates();
        if (isLegendsClub) {
          setSector('Legends club');
          setArea(getZoneFromSector('Legends club'));
        } else {
          const sectorStr = sectorNum.toString();
          setSector(sectorStr);
          const zone = getZoneFromSector(sectorNum);
          setArea(zone);
        }
      };
      
      const handleAreaMouseEnter = (e) => {
        if (isLegendsClub) {
          setHoveredSector('Legends club');
        } else {
          setHoveredSector(sectorNum);
        }
        const areaInfo = parseAreaCoords(coords, shape);
        if (areaInfo) {
          setHoveredAreaCoords(areaInfo);
        }
      };
      
      const handleAreaMouseLeave = (e) => {
        setHoveredSector(null);
        setHoveredAreaCoords(null);
      };
      
      clickHandlers.set(area, handleAreaClick);
      touchHandlers.set(area, handleAreaTouch);
      mouseEnterHandlers.set(area, handleAreaMouseEnter);
      mouseLeaveHandlers.set(area, handleAreaMouseLeave);
      
      // Add click handler for desktop
      area.addEventListener('click', handleAreaClick);
      // Add touch handlers for mobile devices
      area.addEventListener('touchstart', handleAreaTouch, { passive: false });
      area.addEventListener('touchend', handleAreaTouch, { passive: false });
      // Add mouse handlers for desktop hover effects
      area.addEventListener('mouseenter', handleAreaMouseEnter);
      area.addEventListener('mouseleave', handleAreaMouseLeave);
      
      // Prevent default navigation
      area.setAttribute('href', 'javascript:void(0)');
    });

    // Cleanup
    return () => {
      clickHandlers.forEach((handler, area) => {
        area.removeEventListener('click', handler);
      });
      touchHandlers.forEach((handler, area) => {
        area.removeEventListener('touchstart', handler);
        area.removeEventListener('touchend', handler);
      });
      mouseEnterHandlers.forEach((handler, area) => {
        area.removeEventListener('mouseenter', handler);
      });
      mouseLeaveHandlers.forEach((handler, area) => {
        area.removeEventListener('mouseleave', handler);
      });
      clickHandlers.clear();
      touchHandlers.clear();
      mouseEnterHandlers.clear();
      mouseLeaveHandlers.clear();
    };
  }, [imageLoaded, getZoneFromSector, parseAreaCoords, ticket, getAvailableSectors, updateMapCoordinates]);

  const updateCompanionAttendee = (index, field, value) => {
    setCompanionAttendees((prev) => {
      const next = [...prev];
      if (!next[index]) next[index] = emptyCompanionAttendee();
      if (field === 'ecardCode') {
        next[index] = { ...emptyCompanionAttendee(), ecardCode: value, ecardVerified: false };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const lookupCompanionByEcard = async (index) => {
    const code = companionAttendees[index]?.ecardCode?.trim();
    if (!code || (showPrimaryTicket && !includeCompanion)) return;

    const codeKey = code.toLowerCase();
    if (primaryEcard.trim().toLowerCase() === codeKey) {
      alert(t('ticketPayment.alertCompanionEcardOwn'));
      setCompanionAttendees((prev) => {
        const next = [...prev];
        next[index] = { ...emptyCompanionAttendee(), ecardCode: code, ecardVerified: false };
        return next;
      });
      return;
    }

    const duplicateIndex = companionAttendees.findIndex(
      (attendee, i) => i !== index && attendee?.ecardCode?.trim().toLowerCase() === codeKey,
    );
    if (duplicateIndex !== -1) {
      alert(t('ticketPayment.alertCompanionEcardDuplicate'));
      setCompanionAttendees((prev) => {
        const next = [...prev];
        next[index] = { ...emptyCompanionAttendee(), ecardCode: code, ecardVerified: false };
        return next;
      });
      return;
    }

    setCompanionEcardLoading((prev) => ({ ...prev, [index]: true }));
    try {
      const res = await api.get('/ticket-orders/lookup-ecard', { params: { code } });
      setCompanionAttendees((prev) => {
        const next = [...prev];
        next[index] = buildCompanionAttendeeFromProfile({
          ...res.data,
          ecardCode: res.data?.ecardCode || code,
        });
        return next;
      });
    } catch (error) {
      console.error('Error looking up companion e-card:', error);
      alert(error.response?.data?.error || t('ticketPayment.alertCompanionEcardNotFound'));
      setCompanionAttendees((prev) => {
        const next = [...prev];
        next[index] = { ...emptyCompanionAttendee(), ecardCode: code, ecardVerified: false };
        return next;
      });
    } finally {
      setCompanionEcardLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const primaryAttendee = useMemo(
    () => (user ? buildPrimaryAttendeeFromUser(user, { ecardCode: primaryEcard, pnrCode: primaryPnr }) : null),
    [user, primaryEcard, primaryPnr],
  );

  const buildAttendeesForOrder = useCallback(() => {
    const attendees = [];
    if (showPrimaryTicket && primaryAttendee) {
      attendees.push({
        role: 'primary',
        ...primaryAttendee,
        ecardCode: primaryEcard.trim(),
        pnrCode: isSubscriber ? primaryPnr.trim() : undefined,
      });
    }
    if (includeCompanion) {
      companionAttendees.forEach((companion) => {
        attendees.push({ role: 'companion', ...companion });
      });
    }
    return attendees;
  }, [
    showPrimaryTicket,
    primaryAttendee,
    primaryEcard,
    primaryPnr,
    isSubscriber,
    includeCompanion,
    companionAttendees,
  ]);

  const travelPreviewOrder = useMemo(
    () => ({ attendees: buildAttendeesForOrder() }),
    [buildAttendeesForOrder],
  );

  const orderPricing = useMemo(
    () =>
      computeTicketOrderPricing({
        ticket,
        sector,
        showPrimaryTicket,
        primaryDateOfBirth: primaryAttendee?.dateOfBirth || user?.dateOfBirth,
        primaryIsMinor: user?.isMinor,
        includeCompanion,
        companionAttendees,
      }),
    [
      ticket,
      sector,
      showPrimaryTicket,
      primaryAttendee?.dateOfBirth,
      user?.dateOfBirth,
      user?.isMinor,
      includeCompanion,
      companionAttendees,
    ],
  );

  const orderBlocked = Boolean(existingOrder);

  const showMinorPriceBreakdown =
    orderPricing.ready
    && orderPricing.minorCount > 0
    && orderPricing.hasMinorTariff
    && orderPricing.minorUnit != null
    && orderPricing.adultUnit != null;

  const minorPriceBreakdown = showMinorPriceBreakdown ? (
    <>
      {orderPricing.adultCount > 0 && (
        <div className="summary-item">
          <span className="summary-label">{t('ticketPayment.unitPriceLabel')}</span>
          <span className="summary-value">
            {t('ticketPayment.summaryAdultLine', {
              count: orderPricing.adultCount,
              price: orderPricing.adultUnit.toFixed(2),
            })}
          </span>
        </div>
      )}
      <div className="summary-item">
        <span className="summary-label">{t('ticketPayment.minorPrice')}</span>
        <span className="summary-value">
          {t('ticketPayment.summaryMinorLine', {
            count: orderPricing.minorCount,
            price: orderPricing.minorUnit.toFixed(2),
          })}
        </span>
      </div>
      {orderPricing.savings > 0 && (
        <div className="summary-item">
          <span className="summary-label">{t('ticketPayment.summaryMinorRebate')}</span>
          <span className="summary-value">{orderPricing.savings.toFixed(2)} €</span>
        </div>
      )}
    </>
  ) : null;

  const handleTravelFormSaved = () => {
    alert(t('ticketTravel.submitSuccess'));
    navigate('/billets');
  };

  const formatOrderDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return t('ticketPayment.statusConfirmed');
      case 'cancelled':
        return t('ticketPayment.statusCancelled');
      default:
        return t('ticketPayment.statusPending');
    }
  };

  const handleSendRequest = async () => {
    if (!user) {
      alert(t('ticketPayment.alertLogin'));
      navigate('/login');
      return;
    }

    if (!ticket || !ticket._id) {
      alert(t('ticketPayment.alertMissingTicket'));
      return;
    }

    // For home matches, require area selection
    if (ticket.isHome && !area) {
      alert(t('ticketPayment.alertSelectZone'));
      return;
    }

    if (includeCompanion) {
      for (let i = 0; i < companionAttendees.length; i++) {
        const a = companionAttendees[i] || {};
        const billetNum = showPrimaryTicket ? i + 2 : i + 1;
        if (!a.ecardVerified) {
          alert(t('ticketPayment.alertCompanionEcardVerify', { number: billetNum }));
          return;
        }
        if (!isCompanionAttendeeComplete(a)) {
          alert(t('ticketPayment.alertCompanionFields', { number: billetNum }));
          return;
        }
      }
    }

    if (orderQuantity === 0) {
      alert(t('ticketPayment.alertMinOneTicket'));
      return;
    }

    if (!showPrimaryTicket && !includeCompanion) {
      alert(t('ticketPayment.alertMinOneCompanion'));
      return;
    }

    if (identityExpiredForPrimary) {
      alert(identityExpiredMessage);
      return;
    }

    if (showPrimaryTicket && !primaryEcard.trim()) {
      alert(t('ticketPayment.alertEcardRequired'));
      return;
    }

    if (showPrimaryTicket && isSubscriber && !primaryPnr.trim()) {
      alert(t('ticketPayment.alertPnrRequired'));
      return;
    }

    if (!paymentMethod) {
      alert(t('ticketPayment.alertPaymentMethod'));
      return;
    }
    if (!paymentCurrency) {
      alert(t('ticketPayment.alertPaymentCurrency'));
      return;
    }

    let travelInformation;
    if (travelFormRequired) {
      const travelError = travelFormRef.current?.validate();
      if (travelError) {
        alert(travelError);
        document.querySelector('.ticket-travel-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      travelInformation = travelFormRef.current?.getPayload();
    }

    setSubmitting(true);
    try {
      const attendees = buildAttendeesForOrder();

      const orderData = {
        ticketId: ticket._id,
        quantity: attendees.length,
        attendees,
        paymentMethod,
        paymentCurrency,
      };

      if (travelFormRequired && travelInformation) {
        orderData.travelInformation = travelInformation;
      }
      
      // For home matches, include area and sector
      if (ticket.isHome) {
        orderData.area = area;
        orderData.sector = sector;
      } else {
        // For away matches, set a default area value
        orderData.area = 'Match à l\'extérieur';
      }

      const response = await api.post('/ticket-orders', orderData);

      if (response.data) {
        alert(t('ticketPayment.alertSuccess'));
        navigate('/billets');
      }
    } catch (error) {
      console.error('Error sending ticket request:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 409 || error.response?.data?.alreadyOrdered) {
        try {
          const checkRes = await api.get(`/ticket-orders/check/${ticket._id}`);
          if (checkRes.data?.order) {
            setExistingOrder(checkRes.data.order);
          } else {
            setExistingOrder(error.response?.data?.order || { status: 'pending' });
          }
        } catch {
          setExistingOrder(error.response?.data?.order || { status: 'pending' });
        }
      }
      const apiError = error.response?.data;
      const errorMessage =
        apiError?.code === 'IDENTITY_EXPIRED' && apiError?.error
          ? apiError.error
          : apiError?.error || apiError?.message || error.message || t('ticketPayment.alertError');
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => formatMatchDate(dateString, locale);

  const companionPluralSuffix = (count) =>
    count > 1 ? t('ticketPayment.companionPlural') : t('ticketPayment.companionSingular');

  if (!ticket || loading || checkingExistingOrder) {
    return (
      <div className="ticket-payment-page">
        <PageMeta
          title={t('seo.ticketPayment.title')}
          description={t('seo.ticketPayment.description')}
          path="/billets/paiement"
          locale={language}
        />
        <div className="container">
          <div className="loading">{t('ticketPayment.loading')}</div>
        </div>
      </div>
    );
  }

  if (orderBlocked) {
    return (
      <div className="ticket-payment-page">
        <PageMeta
          title={t('seo.ticketPayment.title')}
          description={t('seo.ticketPayment.description')}
          path="/billets/paiement"
          locale={language}
        />
        <div className="container">
          <button className="btn-back" onClick={() => navigate('/billets')}>
            {t('ticketPayment.backToTickets')}
          </button>

          <h1 className="page-title">{t('ticketPayment.alreadySentTitle')}</h1>

          <div className="ticket-order-already-sent">
            <h2>{t('ticketPayment.alreadySentHeading')}</h2>
            <p>{t('ticketPayment.alreadySentBody', { matchTitle: ticket.matchTitle })}</p>
            <ul className="ticket-order-already-sent-details">
              <li>
                <strong>{t('ticketPayment.orderDateLabel')}</strong>{' '}
                {formatOrderDate(existingOrder?.orderDate)}
              </li>
              <li>
                <strong>{t('ticketPayment.statusLabel')}</strong>{' '}
                {existingOrder?.statusLabel || getOrderStatusLabel(existingOrder?.status)}
              </li>
              <li>
                <strong>{t('ticketPayment.quantityLabel')}</strong>{' '}
                {existingOrder?.quantity || 1} {t('ticketPayment.ticketsUnit')}
              </li>
            </ul>
            <p className="ticket-order-already-sent-note">
              {t('ticketPayment.alreadySentNote')}
            </p>
            {!travelFormRequired && (
              <button type="button" className="btn-continue" onClick={() => navigate('/billets')}>
                {t('ticketPayment.backToList')}
              </button>
            )}
          </div>

          {travelFormRequired && existingOrder?._id && (
            <>
              <p className="ticket-travel-existing-note">{t('ticketTravel.alreadySentTravelNote')}</p>
              <TicketOrderTravelForm
                order={{
                  _id: existingOrder._id,
                  attendees: existingOrder.attendees || [],
                  travelInformation: existingOrder.travelInformation,
                }}
                onSaved={handleTravelFormSaved}
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-payment-page">
      <PageMeta
        title={t('seo.ticketPayment.title')}
        description={t('seo.ticketPayment.description')}
        path="/billets/paiement"
        locale={language}
      />
      <div className="container">
        <button className="btn-back" onClick={() => navigate('/billets')}>
          {t('ticketPayment.backToTickets')}
        </button>

        <h1 className="page-title">{t('ticketPayment.pageTitle')}</h1>

        {identityExpiredForPrimary && (
          <div className="ticket-identity-expired-notice" role="alert">
            {identityExpiredMessage}
          </div>
        )}

        {/* Match Info */}
        <div className="match-info">
          <div className="match-teams-logos">
            {(() => {
              const { homeLogo, awayLogo, homeTeam, awayTeam } = getMatchLogos(ticket.matchTitle);
              return (
                <>
                  <div className="team-logo-container">
                    <img src={homeLogo} alt={homeTeam || 'Home'} className="team-logo-large" />
                    <span className="team-name">{homeTeam}</span>
                  </div>
                  <div className="vs-section-large">
                    {ticket.competition && getCompetitionLogo(ticket.competition) && (
                      <img 
                        src={getCompetitionLogo(ticket.competition)} 
                        alt={ticket.competition || 'Competition'} 
                        className="competition-logo-large"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  <span className="vs-separator-large">VS</span>
                  </div>
                  <div className="team-logo-container">
                    <img src={awayLogo} alt={awayTeam || 'Away'} className="team-logo-large" />
                    <span className="team-name">{awayTeam}</span>
                  </div>
                </>
              );
            })()}
          </div>
          {ticket.competition && (
            <p style={{ textAlign: 'center', color: 'var(--text-gray)', marginTop: '0.5rem', fontWeight: 600 }}>
              {ticket.competition}
            </p>
          )}
          <h2>{ticket.matchTitle}</h2>
          <div className="match-details">
            <p><strong>{t('ticketPayment.stadium')}</strong> {ticket.stadium}</p>
            <p><strong>{t('ticketPayment.date')}</strong> {formatDate(ticket.date)}</p>
          </div>
        </div>

        <form
          className="ticket-order-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSendRequest();
          }}
        >
        {/* Titulaires des billets — visible dès le haut de page */}
        <div className="ticket-attendees">
          <h2>{t('ticketPayment.attendeesTitle')}</h2>

          {!showPrimaryTicket && isSubscriber && (
            <div className="attendee-subscriber-notice">
              {t('ticketPayment.subscriberNotice')}
            </div>
          )}

          {showPrimaryTicket && primaryAttendee && (
            <div className="attendee-form attendee-primary">
              <span className="attendee-label">{t('ticketPayment.ticketPrimaryLabel')}</span>
              <p className="attendee-note">{t('ticketPayment.prefilledNote')}</p>
              <div className="attendee-fields">
                <div className="attendee-field">
                  <label>{t('ticketPayment.lastName')}</label>
                  <input type="text" value={primaryAttendee.lastName} readOnly disabled />
                </div>
                <div className="attendee-field">
                  <label>{t('ticketPayment.firstName')}</label>
                  <input type="text" value={primaryAttendee.firstName} readOnly disabled />
                </div>
                <div className="attendee-field">
                  <label>{t('ticketPayment.juventusCard')}</label>
                  <input type="text" value={primaryAttendee.juventusCard} readOnly disabled placeholder="—" />
                </div>
                <div className="attendee-field">
                  <label>{t('ticketPayment.dateOfBirth')}</label>
                  <input type="date" value={primaryAttendee.dateOfBirth} readOnly disabled />
                </div>
                <div className="attendee-field">
                  <label>{t('ticketPayment.countryResidence')}</label>
                  <input type="text" value={primaryAttendee.country} readOnly disabled />
                </div>
                <div className="attendee-field">
                  <label>{t('ticketPayment.cityResidence')}</label>
                  <input type="text" value={primaryAttendee.city} readOnly disabled />
                </div>
                <div className="attendee-field">
                  <label>{t('ticketPayment.email')}</label>
                  <input type="email" value={primaryAttendee.email} readOnly disabled />
                </div>
                <div className="attendee-field">
                  <label>{t('ticketPayment.mobile')}</label>
                  <input type="text" value={primaryAttendee.phone} readOnly disabled />
                </div>
                <div className="attendee-field">
                  <label htmlFor="primaryEcard">{t('ticketPayment.ecardCode')}</label>
                  <input
                    id="primaryEcard"
                    type="text"
                    name="primaryEcard"
                    value={primaryEcard}
                    onChange={(e) => setPrimaryEcard(e.target.value)}
                    placeholder={t('ticketPayment.placeholderEcardCode')}
                    required
                    autoComplete="off"
                  />
                  <p className="attendee-note">{t('ticketPayment.ecardCodeHelp')}</p>
                </div>
                {isSubscriber && (
                  <div className="attendee-field">
                    <label htmlFor="primaryPnr">{t('ticketPayment.pnrCode')}</label>
                    <input
                      id="primaryPnr"
                      type="text"
                      name="primaryPnr"
                      value={primaryPnr}
                      onChange={(e) => setPrimaryPnr(e.target.value)}
                      placeholder={t('ticketPayment.placeholderPnrCode')}
                      required
                      autoComplete="off"
                    />
                    <p className="attendee-note">{t('ticketPayment.pnrCodeHelp')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="attendee-companion-header">
            {showPrimaryTicket && (
              <label className="attendee-companion-toggle">
                <input
                  type="checkbox"
                  checked={includeCompanion}
                  onChange={(e) => setIncludeCompanion(e.target.checked)}
                />
                {t('ticketPayment.companionToggle')}
              </label>
            )}
            {includeCompanion && (
              <button type="button" className="btn-add-companion" onClick={addCompanionForm}>
                {t('ticketPayment.addCompanion')}
              </button>
            )}
          </div>

          {companionAttendees.map((attendee, index) => {
            const billetNumber = showPrimaryTicket ? index + 2 : index + 1;
            const formDisabled = showPrimaryTicket && !includeCompanion;
            const companionRequired = !formDisabled;
            const companionLocked = companionRequired && attendee.ecardVerified;
            const ecardLoading = Boolean(companionEcardLoading[index]);
            return (
              <div
                key={index}
                className={`attendee-form attendee-companion${formDisabled ? ' attendee-form-inactive' : ''}`}
              >
                <div className="attendee-form-title-row">
                  <span className="attendee-label">
                    {t('ticketPayment.ticketCompanionLabel', { number: billetNumber })}
                  </span>
                  {includeCompanion && companionAttendees.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-companion"
                      onClick={() => removeCompanionForm(index)}
                    >
                      {t('ticketPayment.remove')}
                    </button>
                  )}
                </div>
                {formDisabled && (
                  <p className="attendee-note">
                    {t('ticketPayment.companionDisabledNote')}
                  </p>
                )}
                {companionLocked && (
                  <p className="attendee-note">{t('ticketPayment.companionPrefilledNote')}</p>
                )}
                <div className="attendee-fields">
                  <div className="attendee-field">
                    <label>{t('ticketPayment.ecardCode')}</label>
                    <input
                      type="text"
                      value={attendee.ecardCode}
                      onChange={(e) => updateCompanionAttendee(index, 'ecardCode', e.target.value)}
                      onBlur={() => lookupCompanionByEcard(index)}
                      placeholder={t('ticketPayment.placeholderEcardCode')}
                      disabled={formDisabled || ecardLoading}
                      required={companionRequired}
                      autoComplete="off"
                    />
                    <p className="attendee-note">
                      {ecardLoading
                        ? t('ticketPayment.companionEcardLooking')
                        : t('ticketPayment.companionEcardHelp')}
                    </p>
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.lastName')}</label>
                    <input
                      type="text"
                      value={attendee.lastName}
                      onChange={(e) => updateCompanionAttendee(index, 'lastName', e.target.value)}
                      placeholder={t('ticketPayment.placeholderLastName')}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                      required={companionRequired}
                    />
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.firstName')}</label>
                    <input
                      type="text"
                      value={attendee.firstName}
                      onChange={(e) => updateCompanionAttendee(index, 'firstName', e.target.value)}
                      placeholder={t('ticketPayment.placeholderFirstName')}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                      required={companionRequired}
                    />
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.juventusCard')}</label>
                    <input
                      type="text"
                      value={attendee.juventusCard}
                      onChange={(e) => updateCompanionAttendee(index, 'juventusCard', e.target.value)}
                      placeholder={t('ticketPayment.placeholderJuventusCard')}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                    />
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.dateOfBirth')}</label>
                    <input
                      type="date"
                      value={attendee.dateOfBirth}
                      onChange={(e) => updateCompanionAttendee(index, 'dateOfBirth', e.target.value)}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                      required={companionRequired}
                    />
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.countryResidence')}</label>
                    <input
                      type="text"
                      value={attendee.country}
                      onChange={(e) => updateCompanionAttendee(index, 'country', e.target.value)}
                      placeholder={t('ticketPayment.placeholderCountry')}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                      required={companionRequired}
                    />
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.cityResidence')}</label>
                    <input
                      type="text"
                      value={attendee.city}
                      onChange={(e) => updateCompanionAttendee(index, 'city', e.target.value)}
                      placeholder={t('ticketPayment.placeholderCity')}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                      required={companionRequired}
                    />
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.email')}</label>
                    <input
                      type="email"
                      value={attendee.email}
                      onChange={(e) => updateCompanionAttendee(index, 'email', e.target.value)}
                      placeholder={t('ticketPayment.placeholderEmail')}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                      required={companionRequired}
                    />
                  </div>
                  <div className="attendee-field">
                    <label>{t('ticketPayment.mobile')}</label>
                    <input
                      type="tel"
                      value={attendee.phone}
                      onChange={(e) => updateCompanionAttendee(index, 'phone', e.target.value)}
                      placeholder={t('ticketPayment.placeholderPhone')}
                      disabled={formDisabled || companionLocked}
                      readOnly={companionLocked}
                      required={companionRequired}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Area and Sector Selection - ONLY FOR HOME MATCHES */}
        {/* For home matches: Show Allianz Stadium map with sector selection */}
        {/* For away matches: Show only single price (see else block below) */}
        {ticket.isHome ? (
          <div className="ticket-selection">
            <h2>{t('ticketPayment.zoneSelectionTitle')}</h2>
            <div className="stadium-map-container">
            <div className={isMobile ? 'stadium-map-wrapper-mobile' : 'stadium-map-wrapper'}>
              <img
                ref={stadiumImageRef}
                src={stadiumPlan}
                alt={t('ticketPayment.stadiumMapAlt')}
                className={isMobile ? 'stadium-plan-image-mobile' : 'stadium-plan-image'}
                useMap={isMobile ? undefined : "#stadium-map"}
                onClick={isMobile ? handleImageClick : undefined}
                onTouchStart={isMobile ? handleImageTouch : undefined}
                onTouchEnd={isMobile ? handleImageTouch : undefined}
                style={isMobile ? { cursor: 'pointer', pointerEvents: 'auto', userSelect: 'none', WebkitUserSelect: 'none' } : {}}
                onLoad={(e) => {
                  setImageLoaded(true);
                  const img = e.target;
                  // Use natural dimensions for the viewBox (coordinate system)
                  setImageDimensions({
                    width: img.naturalWidth || 775,
                    height: img.naturalHeight || 615
                  });
                  // Use getBoundingClientRect for accurate displayed dimensions
                  setTimeout(() => {
                    const rect = img.getBoundingClientRect();
                    const displayWidth = rect.width || img.clientWidth || img.offsetWidth || img.naturalWidth || 775;
                    const displayHeight = rect.height || img.clientHeight || img.offsetHeight || img.naturalHeight || 615;
                    setDisplayDimensions({
                      width: displayWidth,
                      height: displayHeight
                    });
                    
                    // Calculate responsive SVG dimensions for mobile
                    if (window.innerWidth <= 768) {
                      const baseViewportWidth = 390; // iPhone 12 Pro width
                      const baseSvgWidth = 719;
                      const baseSvgHeight = 580;
                      const currentViewportWidth = window.innerWidth || document.documentElement.clientWidth;
                      const scaleFactor = currentViewportWidth / baseViewportWidth;
                      
                      // Calculate SVG dimensions based on viewport, not image size
                      const svgWidth = Math.round(baseSvgWidth * scaleFactor);
                      const svgHeight = Math.round(baseSvgHeight * scaleFactor);
                      
                      console.log('Setting SVG dimensions on load (mobile):', {
                        viewportWidth: currentViewportWidth,
                        scaleFactor: scaleFactor.toFixed(3),
                        svgDimensions: { width: svgWidth, height: svgHeight }
                      });
                      
                      setSvgDimensions({
                        width: svgWidth,
                        height: svgHeight
                      });
                    } else {
                      setSvgDimensions({
                        width: displayWidth,
                        height: displayHeight
                      });
                    }
                  }, 50);
                }}
              />
              {/* SVG Overlay for hover effects */}
              {imageLoaded && stadiumImageRef.current && displayDimensions && svgDimensions && (() => {
                // Use svgDimensions which is calculated responsively
                const imgWidth = svgDimensions.width;
                const imgHeight = svgDimensions.height;
                
                console.log('Rendering SVG overlay:', {
                  isMobile,
                  svgDimensions: { width: imgWidth, height: imgHeight },
                  displayDimensions: { width: displayDimensions.width, height: displayDimensions.height },
                  viewportWidth: window.innerWidth
                });
                
                return (
                  <svg
                    key={`svg-${imgWidth}-${imgHeight}-${isMobile}`}
                    className="stadium-overlay"
                    style={{
                      width: `${imgWidth}px`,
                      height: `${imgHeight}px`,
                      position: 'absolute',
                      top: '0',
                      left: '0'
                    }}
                    viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                    preserveAspectRatio="none"
                  >
                  {hoveredAreaCoords && (
                    <g>
                      {hoveredAreaCoords.type === 'rect' && (
                        <rect
                          x={hoveredAreaCoords.x}
                          y={hoveredAreaCoords.y}
                          width={hoveredAreaCoords.width}
                          height={hoveredAreaCoords.height}
                          fill="rgba(0, 0, 0, 0.4)"
                          className="sector-hover-overlay"
                        />
                      )}
                      {hoveredAreaCoords.type === 'poly' && (
                        <polygon
                          points={hoveredAreaCoords.points.map(p => `${p.x},${p.y}`).join(' ')}
                          fill="rgba(0, 0, 0, 0.4)"
                          className="sector-hover-overlay"
                        />
                      )}
                      {(sector === hoveredSector?.toString() || (sector === 'Legends club' && hoveredSector === 'Legends club')) && (
                        <>
                          {hoveredAreaCoords.type === 'rect' && (
                            <rect
                              x={hoveredAreaCoords.x}
                              y={hoveredAreaCoords.y}
                              width={hoveredAreaCoords.width}
                              height={hoveredAreaCoords.height}
                              fill="rgba(184, 155, 95, 0.3)"
                              stroke="rgba(184, 155, 95, 0.8)"
                              strokeWidth="2"
                              className="sector-selected-overlay"
                            />
                          )}
                          {hoveredAreaCoords.type === 'poly' && (
                            <polygon
                              points={hoveredAreaCoords.points.map(p => `${p.x},${p.y}`).join(' ')}
                              fill="rgba(184, 155, 95, 0.3)"
                              stroke="rgba(184, 155, 95, 0.8)"
                              strokeWidth="2"
                              className="sector-selected-overlay"
                            />
                          )}
                        </>
                      )}
                    </g>
                  )}
                  {/* Show selected sector(s) – for home matches, merged groups (106-109, 206-210, 118-122, 222-228) highlight together */}
                  {sector && !hoveredSector && (() => {
                    const mapElement = document.getElementById('stadium-map');
                    if (!mapElement) return null;
                    const sectorsToHighlight = ticket.isHome && sector !== 'Legends club'
                      ? (getMergedSectorStringsForHome(parseInt(sector, 10)) || [sector])
                      : [sector];
                    return sectorsToHighlight.map((sec) => {
                      const selectedArea = Array.from(mapElement.querySelectorAll('area')).find(
                        a => a.getAttribute('title') === sec
                      );
                      if (!selectedArea) return null;
                      const coords = selectedArea.getAttribute('coords');
                      const shape = selectedArea.getAttribute('shape');
                      const areaInfo = parseAreaCoords(coords, shape);
                      if (!areaInfo) return null;
                      if (areaInfo.type === 'rect') {
                        return (
                          <rect
                            key={sec}
                            x={areaInfo.x}
                            y={areaInfo.y}
                            width={areaInfo.width}
                            height={areaInfo.height}
                            fill="rgba(184, 155, 95, 0.2)"
                            stroke="rgba(184, 155, 95, 0.6)"
                            strokeWidth="2"
                            className="sector-selected-overlay"
                          />
                        );
                      }
                      if (areaInfo.type === 'poly') {
                        return (
                          <polygon
                            key={sec}
                            points={areaInfo.points.map(p => `${p.x},${p.y}`).join(' ')}
                            fill="rgba(184, 155, 95, 0.2)"
                            stroke="rgba(184, 155, 95, 0.6)"
                            strokeWidth="2"
                            className="sector-selected-overlay"
                          />
                        );
                      }
                      return null;
                    });
                  })()}
                  {/* Gray out unavailable sectors */}
                  {(() => {
                    const availableSectors = getAvailableSectors();
                    const mapElement = document.getElementById('stadium-map');
                    if (!mapElement) return null;
                    
                    return Array.from(mapElement.querySelectorAll('area')).map(area => {
                      const sectorNumStr = area.getAttribute('title');
                      const isLegendsClub = sectorNumStr && sectorNumStr.toLowerCase().includes('legends');
                      const sectorKey = isLegendsClub ? 'Legends club' : sectorNumStr;
                      const isAvailable = availableSectors.has(sectorKey);
                      
                      if (isAvailable) return null; // Don't gray out available sectors
                      
                      const coords = area.getAttribute('coords');
                      const shape = area.getAttribute('shape');
                      const areaInfo = parseAreaCoords(coords, shape);
                      if (!areaInfo) return null;
                      
                      if (areaInfo.type === 'rect') {
                        return (
                          <rect
                            key={`unavailable-${sectorKey}`}
                            x={areaInfo.x}
                            y={areaInfo.y}
                            width={areaInfo.width}
                            height={areaInfo.height}
                            fill="rgba(0, 0, 0, 0.6)"
                            className="sector-unavailable-overlay"
                          />
                        );
                      } else if (areaInfo.type === 'poly') {
                        return (
                          <polygon
                            key={`unavailable-${sectorKey}`}
                            points={areaInfo.points.map(p => `${p.x},${p.y}`).join(' ')}
                            fill="rgba(0, 0, 0, 0.6)"
                            className="sector-unavailable-overlay"
                          />
                        );
                      }
                      return null;
                    }).filter(Boolean);
                  })()}
                  </svg>
                );
              })()}
              <map name="stadium-map" id="stadium-map" style={{ display: isMobile ? 'none' : 'block' }}>
                <area target="" alt="111" title="111" href="#" coords="241,95,156,121,210,175,236,167" shape="poly" />
                <area target="" alt="117" title="117" href="#" coords="549,95,624,126,571,176,545,168" shape="poly" />
                <area target="" alt="119" title="119" href="#" coords="579,207,648,269" shape="rect" />
                <area target="" alt="120" title="120" href="#" coords="578,273,647,334" shape="rect" />
                <area target="" alt="121" title="121" href="#" coords="580,339,648,393" shape="rect" />
                <area target="" alt="109" title="109" href="#" coords="124,203,199,264" shape="rect" />
                <area target="" alt="108" title="108" href="#" coords="124,269,198,325" shape="rect" />
                <area target="" alt="107" title="107" href="#" coords="125,330,197,388" shape="rect" />
                <area target="" alt="201" title="201" href="#" coords="355,551,403,515" shape="rect" />
                <area target="" alt="232" title="232" href="#" coords="475,547,412,516" shape="rect" />
                <area target="" alt="231" title="231" href="#" coords="482,510,544,549" shape="rect" />
                <area target="" alt="202" title="202" href="#" coords="343,515,294,545" shape="rect" />
                <area target="" alt="203" title="203" href="#" coords="288,516,236,551" shape="rect" />
                <area target="" alt="204" title="204" href="#" coords="233,508,182,552" shape="rect" />
                <area target="" alt="124" title="124" href="#" coords="528,436,504,464" shape="rect" />
                <area target="" alt="125" title="125" href="#" coords="493,434,470,456" shape="rect" />
                <area target="" alt="102" title="102" href="#" coords="343,435,307,467" shape="rect" />
                <area target="" alt="113" title="113" href="#" coords="301,92,357,169" shape="rect" />
                <area target="" alt="114" title="114" href="#" coords="419,170,362,91" shape="rect" />
                <area target="" alt="115" title="115" href="#" coords="425,92,478,169" shape="rect" />
                <area target="" alt="116" title="116" href="#" coords="486,94,542,167" shape="rect" />
                <area target="" alt="112" title="112" href="#" coords="243,95,299,167" shape="rect" />
                <area target="" alt="210" title="210" href="#" coords="81,205,119,262" shape="rect" />
                <area target="" alt="209" title="209" href="#" coords="78,322,119,268" shape="rect" />
                <area target="" alt="208" title="208" href="#" coords="119,329,75,393" shape="rect" />
                <area target="" alt="224" title="224" href="#" coords="695,206,657,269" shape="rect" />
                <area target="" alt="225" title="225" href="#" coords="695,278,657,331" shape="rect" />
                <area target="" alt="226" title="226" href="#" coords="695,342,658,396" shape="rect" />
                <area target="" alt="118" title="118" href="#" coords="629,129,650,200,582,200,576,178" shape="poly" />
                <area target="" alt="110" title="110" href="#" coords="155,128,201,175,196,195,126,196" shape="poly" />
                <area target="" alt="106" title="106" href="#" coords="197,397,123,396,153,477,207,425" shape="poly" />
                <area target="" alt="211" title="211" href="#" coords="90,142,84,198,119,200,125,161" shape="poly" />
                <area target="" alt="212" title="212" href="#" coords="96,132,122,95,145,121,129,152" shape="poly" />
                <area target="" alt="213" title="213" href="#" coords="128,86,170,61,184,98,147,117" shape="poly" />
                <area target="" alt="214" title="214" href="#" coords="176,58,234,50,238,82,187,95" shape="poly" />
                <area target="" alt="207" title="207" href="#" coords="119,400,76,402,85,457,126,446" shape="poly" />
                <area target="" alt="206" title="206" href="#" coords="92,466,118,507,145,482,129,451" shape="poly" />
                <area target="" alt="205" title="205" href="#" coords="121,509,171,543,175,499,153,484" shape="poly" />
                <area target="" alt="215" title="215" href="#" coords="241,52,285,52,292,89,249,87" shape="poly" />
                <area target="" alt="216" title="216" href="#" coords="297,46,357,88" shape="rect" />
                <area target="" alt="217" title="217" href="#" coords="368,49,419,86" shape="rect" />
                <area target="" alt="218" title="218" href="#" coords="424,48,478,85" shape="rect" />
                <area target="" alt="219" title="219" href="#" coords="491,47,538,88" shape="rect" />
                <area target="" alt="220" title="220" href="#" coords="546,45,594,93" shape="rect" />
                <area target="" alt="221" title="221" href="#" coords="606,65,647,89,618,120,595,105" shape="poly" />
                <area target="" alt="222" title="222" href="#" coords="655,96,681,136,650,154,631,120" shape="poly" />
                <area target="" alt="223" title="223" href="#" coords="679,144,692,196,655,195,652,163" shape="poly" />
                <area target="" alt="227" title="227" href="#" coords="655,401,650,440,687,454,697,405" shape="poly" />
                <area target="" alt="228" title="228" href="#" coords="650,450,682,465,656,505,626,478" shape="poly" />
                <area target="" alt="229" title="229" href="#" coords="649,507,609,535,593,506,622,482" shape="poly" />
                <area target="" alt="230" title="230" href="#" coords="586,507,601,540,546,547,550,513" shape="poly" />
                <area target="" alt="Legends club" title="Legends club" href="#" coords="193,470,592,501" shape="rect" />
                <area target="" alt="103" title="103" href="#" coords="302,428,277,463" shape="rect" />
                <area target="" alt="104" title="104" href="#" coords="274,430,248,464" shape="rect" />
                <area target="" alt="105" title="105" href="#" coords="245,429,206,462" shape="rect" />
                <area target="" alt="126" title="126" href="#" coords="465,436,430,464" shape="rect" />
                <area target="" alt="123" title="123" href="#" coords="568,429,538,464" shape="rect" />
                <area target="" alt="101" title="101" href="#" coords="347,437,422,463" shape="rect" />
                <area target="" alt="122" title="122" href="#" coords="579,399,643,402,623,469,574,428" shape="poly" />
              </map>
            </div>
            {sector && (
              <div className="selected-sector-info">
                <p><strong>{t('ticketPayment.selectedSector')}</strong> {sector}</p>
                {area && <p><strong>{t('ticketPayment.zone')}</strong> {area}</p>}
                {(() => {
                  const { price, minorPrice } = getSectorPricing(ticket, sector);
                  return (
                    <>
                      {price !== null ? (
                        <p><strong>{t('ticketPayment.price')}</strong> {price.toFixed(2)} €</p>
                      ) : null}
                      {minorPrice !== null ? (
                        <p><strong>{t('ticketPayment.minorPrice')}</strong> {minorPrice.toFixed(2)} €</p>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            )}
            {!sector && (
              <div className="sector-instruction">
                <p>{t('ticketPayment.sectorInstruction')}</p>
              </div>
            )}
          </div>
        </div>
        ) : (
          /* AWAY MATCHES: Single price display only (no stadium map) */
          <div className="ticket-selection">
            <h2>{t('ticketPayment.awayPriceTitle')}</h2>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem', textAlign: 'center' }}>
              {t('ticketPayment.awayPriceNote')}
            </p>
            <div style={{ 
              padding: '2rem', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              textAlign: 'center'
            }}>
              {ticket.price ? (
                <>
                  <p style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#666' }}>
                  {t('ticketPayment.unitPrice')}
                </p>
                <p style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--juve-black)', marginBottom: '1rem' }}>
                  {ticket.price.toFixed(2)} €
                </p>
                {(() => {
                  const { minorPrice } = getAwayMatchPricing(ticket);
                  return minorPrice !== null ? (
                    <p style={{ fontSize: '1rem', color: '#666', marginBottom: 0 }}>
                      <strong>{t('ticketPayment.minorPrice')}</strong> {minorPrice.toFixed(2)} €
                    </p>
                  ) : null;
                })()}
                </>
              ) : (
                <p style={{ fontSize: '1rem', color: '#e74c3c' }}>
                  {t('ticketPayment.priceNotDefined')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Multiple tickets info */}
        <div className="multi-ticket-info">
          <h2>{t('ticketPayment.companionInfoTitle')}</h2>
          <p>{t('ticketPayment.companionInfoBody')}</p>
        </div>

        {/* Ticket Summary */}
        <div className="ticket-summary">
          <h2>{t('ticketPayment.summaryTitle')}</h2>
          <div className="summary-content">
            <div className="summary-item">
              <span className="summary-label">{t('ticketPayment.match')}</span>
              <span className="summary-value">{ticket.matchTitle}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('ticketPayment.stadium')}</span>
              <span className="summary-value">{ticket.stadium}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('ticketPayment.date')}</span>
              <span className="summary-value">{formatDate(ticket.date)}</span>
            </div>
            {showPrimaryTicket && (
              <>
                <div className="summary-item">
                  <span className="summary-label">{t('ticketPayment.ecardCodeSummary')} (Billet 1)</span>
                  <span className="summary-value">{primaryEcard.trim() || t('ticketPayment.zoneNotSelected')}</span>
                </div>
                {isSubscriber && (
                  <div className="summary-item">
                    <span className="summary-label">{t('ticketPayment.pnrCodeSummary')} (Billet 1)</span>
                    <span className="summary-value">{primaryPnr.trim() || t('ticketPayment.zoneNotSelected')}</span>
                  </div>
                )}
              </>
            )}
            {ticket.isHome && (
              <>
                <div className="summary-item">
                  <span className="summary-label">{t('ticketPayment.zone')}</span>
                  <span className="summary-value">{area || t('ticketPayment.zoneNotSelected')}</span>
                </div>
                {sector && (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">{t('ticketPayment.sector')}</span>
                      <span className="summary-value">{sector}</span>
                    </div>
                    {showMinorPriceBreakdown ? (
                      minorPriceBreakdown
                    ) : (
                      (() => {
                        const price = getSectorPrice(sector);
                        return price !== null ? (
                          <div className="summary-item">
                            <span className="summary-label">{t('ticketPayment.unitPriceLabel')}</span>
                            <span className="summary-value">{price.toFixed(2)} €</span>
                          </div>
                        ) : null;
                      })()
                    )}
                  </>
                )}
              </>
            )}
            {!ticket.isHome && ticket.price != null ? (
              showMinorPriceBreakdown ? (
                minorPriceBreakdown
              ) : (
                <div className="summary-item">
                  <span className="summary-label">{t('ticketPayment.unitPriceLabel')}</span>
                  <span className="summary-value">{Number(ticket.price).toFixed(2)} €</span>
                </div>
              )
            ) : null}
            <div className="summary-item summary-item-quantity">
              <span className="summary-label">{t('ticketPayment.ticketCount')}</span>
              <span className="summary-value summary-quantity-value">
                <span>
                  {orderQuantity}{' '}
                  {orderQuantity > 1 ? t('ticketPayment.ticketPlural') : t('ticketPayment.ticketSingular')}
                  {showPrimaryTicket && includeCompanion &&
                    ` ${t('ticketPayment.quantityYouPlusCompanion', {
                      count: activeCompanionCount,
                      companionPlural: companionPluralSuffix(activeCompanionCount),
                    })}`}
                  {!showPrimaryTicket && includeCompanion &&
                    ` ${t('ticketPayment.quantityCompanionOnly', {
                      count: activeCompanionCount,
                      companionPlural: companionPluralSuffix(activeCompanionCount),
                    })}`}
                </span>
              </span>
            </div>
            {orderPricing.ready && orderPricing.total != null ? (
              <div className="summary-item summary-total">
                <span className="summary-label">{t('ticketPayment.total')}</span>
                <span className="summary-value">{orderPricing.total.toFixed(2)} €</span>
              </div>
            ) : null}
          </div>
        </div>

        {travelFormRequired && orderQuantity > 0 && (
          <TicketOrderTravelForm
            ref={travelFormRef}
            embedded
            order={travelPreviewOrder}
          />
        )}

        <div className="payment-methods-selection">
          <h2>{t('ticketPayment.paymentSectionTitle')}</h2>
          <p className="attendee-note" style={{ marginBottom: '1.25rem' }}>
            {t('ticketPayment.paymentSectionIntro')}{' '}
            <Link to="/paiement-et-comptes">{t('ticketPayment.paymentSectionLink')}</Link>.
          </p>
          <div className="form-row" style={{ display: 'grid', gap: '1rem', maxWidth: '480px' }}>
            <div className="form-group">
              <label htmlFor="ticketPaymentMethod">{t('ticketPayment.paymentMethodLabel')}</label>
              <select
                id="ticketPaymentMethod"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                required
              >
                <option value="">{t('ticketPayment.selectPayment')}</option>
                <optgroup label={t('ticketPayment.optgroupEbanking')}>
                  <option value="E-banking">{t('ticketPayment.optEbanking')}</option>
                </optgroup>
                <optgroup label={t('ticketPayment.optgroupTwint')}>
                  <option value="Twint">{t('ticketPayment.optTwint')}</option>
                </optgroup>
                <optgroup label={t('ticketPayment.optgroupRevolut')}>
                  <option value="Carte Revolut">{t('ticketPayment.optRevolut')}</option>
                </optgroup>
                <optgroup label={t('ticketPayment.optgroupCredit')}>
                  <option value="Carte de crédit">{t('ticketPayment.optCredit')}</option>
                </optgroup>
              </select>
            </div>
            <div className="form-group">
              <label>{t('ticketPayment.paymentCurrencyLabel')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="ticketPaymentCurrency"
                    value="CHF"
                    checked={paymentCurrency === 'CHF'}
                    onChange={() => setPaymentCurrency('CHF')}
                  />
                  <span>{t('ticketPayment.currencyChf')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="ticketPaymentCurrency"
                    value="EUR"
                    checked={paymentCurrency === 'EUR'}
                    onChange={() => setPaymentCurrency('EUR')}
                    disabled={paymentMethod === 'Twint'}
                  />
                  <span>{t('ticketPayment.currencyEur')}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Info Message */}
        <div className="request-info">
          <p>{t('ticketPayment.requestInfo')}</p>
        </div>

        {/* Action Buttons */}
        <div className="payment-actions">
          <button className="btn-cancel" type="button" onClick={() => navigate('/billets')}>
            {t('ticketPayment.cancel')}
          </button>
          <button 
            type="submit"
            className="btn-continue" 
            disabled={
              submitting ||
              !user ||
              orderBlocked ||
              identityExpiredForPrimary ||
              (ticket.isHome && !area) ||
              (!ticket.isHome && !ticket.price) ||
              orderQuantity === 0 ||
              (showPrimaryTicket && !primaryEcard.trim()) ||
              (showPrimaryTicket && isSubscriber && !primaryPnr.trim()) ||
              !paymentMethod ||
              !paymentCurrency
            }
          >
            {submitting ? t('ticketPayment.submitting') : t('ticketPayment.submit')}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default TicketPayment;
